import React from 'react';
import styles from './PerformancePie.module.scss';
import Tooltip from '@mui/material/Tooltip';

export const PIE_COLORS = {
  good: 'var(--color-success)',
  fast: 'var(--color-warning)',
  slow: 'var(--color-danger)',
  rest: 'var(--color-text-5)',
};

export interface PerformancePieProps {
  good: number;
  fast: number;
  slow: number;
  /** Optional total target reps. When larger than performed, remainder is gray. */
  target?: number;
  /** Optional label shown under the bar (e.g., ECCENTRIC / CONCENTRIC) */
  subtitle?: string;
  /** Visual width in px */
  width?: number;
  /** Visual height in px */
  height?: number;
}

const clampNonNegative = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

const PerformancePie: React.FC<PerformancePieProps> = ({
  good,
  fast,
  slow,
  target,
  subtitle,
  width,
  height = 16,
}) => {
  const goodCount = clampNonNegative(good);
  const fastCount = clampNonNegative(fast);
  const slowCount = clampNonNegative(slow);
  const performed = goodCount + fastCount + slowCount;
  const total = Math.max(target ?? performed, performed, 1);
  const rest = Math.max(total - performed, 0);

  const largestColor =
    performed === 0
      ? PIE_COLORS.rest
      : goodCount >= fastCount && goodCount >= slowCount
        ? PIE_COLORS.good
        : fastCount >= goodCount && fastCount >= slowCount
          ? PIE_COLORS.fast
          : PIE_COLORS.slow;

  const goodPct = (goodCount / total) * 100;
  const fastPct = (fastCount / total) * 100;
  const slowPct = (slowCount / total) * 100;
  const restPct = (rest / total) * 100;

  const formatPct = (n: number) => `${Math.round(n)}%`;
  const minLabelPct = 8; // hide inline text if the slice is too small
  const tooltipSlotProps = {
    tooltip: { className: styles.tooltip },
    arrow: { className: styles.tooltipArrow },
  } as const;

  const maxWidth = typeof width === 'number' ? width : undefined;
  const responsiveWidthStyle = maxWidth
    ? { width: '100%', maxWidth }
    : { width: '100%' };

  return (
    <div className={styles.container}>
      <div className={styles.labelsRow} style={responsiveWidthStyle}>
        <div
          className={styles.labelSeg}
          style={{ width: `${goodPct}%`, color: PIE_COLORS.good }}
        >
          {goodPct >= minLabelPct ? formatPct(goodPct) : ''}
        </div>
        <div
          className={styles.labelSeg}
          style={{ width: `${fastPct}%`, color: PIE_COLORS.fast }}
        >
          {fastPct >= minLabelPct ? formatPct(fastPct) : ''}
        </div>
        <div
          className={styles.labelSeg}
          style={{ width: `${slowPct}%`, color: PIE_COLORS.slow }}
        >
          {slowPct >= minLabelPct ? formatPct(slowPct) : ''}
        </div>
        <div
          className={styles.labelSeg}
          style={{ width: `${restPct}%`, color: PIE_COLORS.rest }}
        >
          {restPct >= minLabelPct ? formatPct(restPct) : ''}
        </div>
      </div>
      <div
        className={styles.barContainer}
        style={{ borderColor: largestColor, ...responsiveWidthStyle }}
      >
        <div className={styles.bar} style={{ height }}>
          <Tooltip
            title={`Good ${formatPct(goodPct)}`}
            placement="top"
            arrow
            slotProps={tooltipSlotProps}
          >
            <div
              className={styles.segGood}
              style={{ width: `${goodPct}%`, background: PIE_COLORS.good }}
            />
          </Tooltip>
          <Tooltip
            title={`Fast ${formatPct(fastPct)}`}
            placement="top"
            arrow
            slotProps={tooltipSlotProps}
          >
            <div
              className={styles.segFast}
              style={{ width: `${fastPct}%`, background: PIE_COLORS.fast }}
            />
          </Tooltip>
          <Tooltip
            title={`Slow ${formatPct(slowPct)}`}
            placement="top"
            arrow
            slotProps={tooltipSlotProps}
          >
            <div
              className={styles.segSlow}
              style={{ width: `${slowPct}%`, background: PIE_COLORS.slow }}
            />
          </Tooltip>
          <Tooltip
            title={`Skipped ${formatPct(restPct)}`}
            placement="top"
            arrow
            slotProps={tooltipSlotProps}
          >
            <div
              className={styles.segRest}
              style={{ width: `${restPct}%`, background: PIE_COLORS.rest }}
            />
          </Tooltip>
        </div>
      </div>
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
    </div>
  );
};

export default PerformancePie;
